import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media/constants';
import {
    getLocalParticipant,
    getParticipantById,
    getVirtualScreenshareParticipantOwnerId
} from '../base/participants/functions';
import { getTrackByMediaTypeAndParticipant } from '../base/tracks/functions';

import { ZOOM_SYNC_EPSILON } from './constants';

const STORE_NAME = 'features/zoom-sync';

const DEFAULT_ASPECT_RATIO = 16 / 9;

/**
 * The zoom state that means "not zoomed".
 */
export const IDENTITY_ZOOM_STATE = Object.freeze({
    zoom: 1,
    cx: 0.5,
    cy: 0.5,
    ar: DEFAULT_ASPECT_RATIO
});

/**
 * Rectangle the video picture occupies inside a box, for the given
 * object-fit. Both ends of a sync compute this for their own box, so the
 * normalized coordinates refer to the picture rather than to the box.
 *
 * @param {number} boxWidth - Box width.
 * @param {number} boxHeight - Box height.
 * @param {number} aspectRatio - Video width / height.
 * @param {string} objectFit - 'cover' or 'contain'.
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function getVideoContentRect(boxWidth, boxHeight, aspectRatio, objectFit = 'cover') {
    const ar = aspectRatio > 0 ? aspectRatio : DEFAULT_ASPECT_RATIO;
    const boxAr = boxHeight > 0 ? boxWidth / boxHeight : ar;

    // contain: the picture is bound by the box's smaller relative side;
    // cover: by the larger one.
    const heightBound = objectFit === 'contain' ? boxAr > ar : boxAr < ar;
    const height = heightBound ? boxHeight : boxWidth / ar;
    const width = heightBound ? boxHeight * ar : boxWidth;

    return {
        x: (boxWidth - width) / 2,
        y: (boxHeight - height) / 2,
        width,
        height
    };
}

/**
 * Converts a view transform (CSS: translate(posX, posY) scale(zoom) around the
 * box center) into the picture point shown at the box center, normalized to
 * the picture, plus the zoom. That is what other participants need to show
 * the same region regardless of their own box size.
 *
 * @param {Object} transform - { zoom, posX, posY } in box pixels.
 * @param {Object} box - { width, height } of the transformed box.
 * @param {number} aspectRatio - Video width / height.
 * @param {string} objectFit - 'cover' or 'contain'.
 * @returns {{ zoom: number, cx: number, cy: number, ar: number }}
 */
export function toSyncState(transform, box, aspectRatio, objectFit) {
    const zoom = Number(transform?.zoom) || 1;
    const ar = aspectRatio > 0 ? _round(aspectRatio, 1000) : DEFAULT_ASPECT_RATIO;

    if (zoom <= 1 + ZOOM_SYNC_EPSILON || !(box?.width > 0) || !(box?.height > 0)) {
        return {
            ...IDENTITY_ZOOM_STATE,
            ar
        };
    }

    const rect = getVideoContentRect(box.width, box.height, ar, objectFit);
    const px = (box.width / 2) - ((transform.posX || 0) / zoom);
    const py = (box.height / 2) - ((transform.posY || 0) / zoom);

    return {
        zoom: _round(zoom, 100),
        cx: _round(_clamp01((px - rect.x) / rect.width), 10000),
        cy: _round(_clamp01((py - rect.y) / rect.height), 10000),
        ar
    };
}

/**
 * Inverse of {@link toSyncState} for the receiving box: the CSS transform
 * that puts the sender's center point at this box's center at the same zoom.
 * The pan is limited so the scaled box still covers its container.
 *
 * @param {Object} zoomState - { zoom, cx, cy, ar }.
 * @param {Object} box - { width, height } of the receiving box.
 * @param {string} objectFit - 'cover' or 'contain'.
 * @returns {{ zoom: number, posX: number, posY: number }}
 */
export function toTransform(zoomState, box, objectFit) {
    const zoom = Number(zoomState?.zoom) || 1;

    if (zoom <= 1 + ZOOM_SYNC_EPSILON || !(box?.width > 0) || !(box?.height > 0)) {
        return {
            zoom: 1,
            posX: 0,
            posY: 0
        };
    }

    const rect = getVideoContentRect(box.width, box.height, zoomState.ar, objectFit);
    const px = rect.x + (_clamp01(zoomState.cx) * rect.width);
    const py = rect.y + (_clamp01(zoomState.cy) * rect.height);
    const maxX = (box.width * (zoom - 1)) / 2;
    const maxY = (box.height * (zoom - 1)) / 2;

    return {
        zoom,
        posX: Math.max(-maxX, Math.min(maxX, ((box.width / 2) - px) * zoom)),
        posY: Math.max(-maxY, Math.min(maxY, ((box.height / 2) - py) * zoom))
    };
}

/**
 * Mirrors a zoom state horizontally. Shared states describe the picture as
 * the camera produces it; a preview drawn mirrored (the usual local self
 * view) shows that picture flipped, so a zoom centered on the left of the
 * self view is centered on the right of what everyone else receives. Apply
 * on the way out of a mirrored preview and on the way into one.
 *
 * @param {Object} zoomState - { zoom, cx, cy, ar }.
 * @returns {Object}
 */
export function flipZoomStateX(zoomState) {
    if (!zoomState) {
        return zoomState;
    }

    return {
        ...zoomState,
        cx: _round(1 - _clamp01(zoomState.cx), 10000)
    };
}

/**
 * Whether two zoom states describe the same framing.
 *
 * @param {Object} a - First state.
 * @param {Object} b - Second state.
 * @returns {boolean}
 */
export function isSameZoomState(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }

    const aZoomed = a.zoom > 1 + ZOOM_SYNC_EPSILON;
    const bZoomed = b.zoom > 1 + ZOOM_SYNC_EPSILON;

    if (!aZoomed && !bZoomed) {
        return true;
    }

    return Math.abs(a.zoom - b.zoom) < 0.005
        && Math.abs(a.cx - b.cx) < 0.0005
        && Math.abs(a.cy - b.cy) < 0.0005
        && Math.abs((a.ar || 0) - (b.ar || 0)) < 0.005;
}

/**
 * Parses the attributes of a zoom presence command. Values travel as text.
 *
 * @param {Object} attributes - Command attributes.
 * @returns {Object|undefined} The zoom state, or undefined when malformed.
 */
export function parseZoomCommand(attributes) {
    const zoom = parseFloat(attributes?.zoom);

    if (!Number.isFinite(zoom) || zoom < 1) {
        return undefined;
    }

    const cx = parseFloat(attributes.cx);
    const cy = parseFloat(attributes.cy);
    const ar = parseFloat(attributes.ar);

    return {
        zoom,
        cx: Number.isFinite(cx) ? _clamp01(cx) : 0.5,
        cy: Number.isFinite(cy) ? _clamp01(cy) : 0.5,
        ar: ar > 0 ? ar : DEFAULT_ASPECT_RATIO
    };
}

/**
 * The last value returned by {@link getStageVideo}, reused while it is unchanged.
 *
 * @type {Object|undefined}
 */
let _lastStageVideo;

/**
 * Describes the video currently on the large video: who owns it, whether it
 * is their camera or screenshare, and whether it is the local user's own.
 *
 * @param {Object} state - The redux state.
 * @returns {{ participantId: string, videoType: string, local: boolean }|undefined}
 */
export function getStageVideo(state) {
    const { participantId } = state['features/large-video'] || {};
    const participant = getParticipantById(state, participantId);

    if (!participant) {
        return undefined;
    }

    const tracks = state['features/base/tracks'] || [];
    const localId = getLocalParticipant(state)?.id;
    let ownerId = participant.id;
    let track;

    if (participant.isFakeParticipant === 'LocalScreenShare') {
        ownerId = localId;
        track = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, ownerId);
    } else if (participant.isFakeParticipant === 'RemoteScreenShare') {
        ownerId = getVirtualScreenshareParticipantOwnerId(participant.id);
        track = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, ownerId);
    } else if (participant.isFakeParticipant) {
        // Shared video, whiteboard and the like are not anyone's camera.
        return undefined;
    } else {
        // Same precedence as the large video itself: an unmuted screenshare
        // of this participant is what is on stage, otherwise their camera.
        const screenshare = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, ownerId);

        track = screenshare && !screenshare.muted
            ? screenshare
            : getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, ownerId);
    }

    if (!ownerId || !track || track.muted) {
        return undefined;
    }

    const videoType = track.videoType === VIDEO_TYPE.DESKTOP ? VIDEO_TYPE.DESKTOP : VIDEO_TYPE.CAMERA;
    const local = ownerId === localId;

    // Return the same object while the stage video is unchanged. LargeVideo passes this straight through
    // mapStateToProps, and a fresh object on every call fails react-redux's shallow comparison - which
    // re-rendered the whole large video on every action dispatched anywhere in the app for as long as a video
    // was on stage.
    if (!_lastStageVideo
            || _lastStageVideo.participantId !== ownerId
            || _lastStageVideo.videoType !== videoType
            || _lastStageVideo.local !== local) {
        _lastStageVideo = {
            participantId: ownerId,
            videoType,
            local
        };
    }

    return _lastStageVideo;
}

/**
 * The zoom a remote participant applied to one of their videos, if any.
 *
 * @param {Object} state - The redux state.
 * @param {string} participantId - The participant who owns the video.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @returns {Object|undefined}
 */
export function getRemoteVideoZoom(state, participantId, videoType) {
    return state[STORE_NAME]?.remote?.[participantId]?.[videoType];
}

/**
 * The zoom the local participant applied to one of their own videos.
 *
 * @param {Object} state - The redux state.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @returns {Object|undefined}
 */
export function getLocalVideoZoom(state, videoType) {
    return state[STORE_NAME]?.local?.[videoType];
}

/**
 * The pending 1x / 2x / ... request for the large video, if any.
 *
 * @param {Object} state - The redux state.
 * @returns {{ zoom: number, nonce: number }|undefined}
 */
export function getRequestedViewZoom(state) {
    return state[STORE_NAME]?.requestedViewZoom;
}

/**
 * Whether the local user approved sharing local zoom with all participants.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean|undefined}
 */
export function getLocalZoomShareApproved(state) {
    return state[STORE_NAME]?.localZoomShareApproved;
}

/**
 * Whether a moderator approved sharing moderator pin zoom with all participants.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean|undefined}
 */
export function getModeratorZoomShareApproved(state) {
    return state[STORE_NAME]?.moderatorZoomShareApproved;
}

/**
 * Clamps to [0, 1].
 *
 * @param {number} value - Value.
 * @private
 * @returns {number}
 */
function _clamp01(value) {
    return Math.min(1, Math.max(0, Number(value) || 0));
}

/**
 * Rounds to a number of decimal steps, keeping presence payloads short.
 *
 * @param {number} value - Value.
 * @param {number} steps - Steps per unit (100 = two decimals).
 * @private
 * @returns {number}
 */
function _round(value, steps) {
    return Math.round(value * steps) / steps;
}
